/**
 * orbitalRecipes.js — the six pages of "Images for Design Confirmation" V1.3.3.
 *
 * The reference is
 *   Images for Design Confirmation - O3D-2026-06-18-0001_a.pdf
 * measured in docs/case-report/ORBITAL-REFERENCE-MEASUREMENTS.md. Fidelity
 * decisions in docs/adr/0009; the input contract in docs/adr/0010.
 *
 *   1  Preview Image          CT data block, quality waiver, isometric skull
 *   2  Native skull           six-view grid
 *   3  Orbital volume calc    three composite scenes + a colour key in cc
 *   4  Bilateral orbital      six-view grid, lateral views cropped to the orbit
 *   5  Orbital difference     six-view grid of the difference model alone
 *   6  Difference on trial    one large isometric with two leader callouts
 *
 * Every recipe is `kind: 'custom'` and composes its page with the tools
 * draftReport hands it, so all the template's specifics live here and the shared
 * resolution / conflict / note discipline stays in draft.js.
 *
 * ── Three rules this file exists to hold ────────────────────────────────────
 *
 * **Laterality is never assumed.** The reference case is a LEFT orbit
 * reconstruction. Which side carries the post-plan volume is resolved from the
 * library, and every caption and legend label is derived from what resolved. A
 * right-side case therefore reads "Post plan right orbit" with no code change.
 * This is risk control RC-2 and the reason page 3 is not a fixed three-row list.
 *
 * **The volumes are computed, never typed.** They arrive from volumes.js via
 * `ctx.volumes`, keyed 'role/stage'. A page whose volumes are missing is skipped
 * with a reason rather than drawn with blanks.
 *
 * **The difference and trial-fitment models resolve by ROLE ALONE.** Their stage
 * depends on how they were classified (filename vs operator override), so a
 * role/stage lookup loses them after a correction. See resolveByRole.
 */

import {
  newInfoPage, newGalleryPage, sixViewGridPage, GRID_VIEWS, isRenderableView, waiverBlockFor,
} from './schema.js'
import { CT_FIELDS } from '../sseReport.js'
import { ORBITAL_VOLUME_COLORS, hexToRgb } from './palette.js'
import { formatCc, watertightWarning } from './volumes.js'

/**
 * How translucent the orbital volumes are drawn.
 *
 * The reference draws them see-through — the orbital walls are visible through
 * all three — which is what makes the solid read as being INSIDE the orbit. A
 * starting value; the exact figure is a visual judgement for the design team,
 * as are the albedos in palette.js.
 */
const VOLUME_OPACITY = 0.72

/** Red, for the old-scan study date and the waiver value. The BRAND red. */
const RED = [192, 57, 43]

/**
 * The page-1 CT rows, in the reference's own order.
 *
 * Keys are CT_FIELDS' keys — the same list the SSE report prints — so the two
 * documents can never disagree about what a field is called, and a future
 * autofill from scan metadata needs no remapping. The ORDER is the reference's,
 * which is not CT_FIELDS' order, so it is stated here rather than derived.
 */
export const ORBITAL_CT_ROW_KEYS = [
  'scan_type', 'slice_thickness', 'scan_region', 'contrast', 'patient_age',
  'study_date', 'dicom_received', 'series_name', 'series_type', 'data_type',
]

const CT_LABEL = new Map(CT_FIELDS.map(([label, key]) => [key, label]))
/** The reference writes "DICOM received date", where CT_FIELDS says "DICOM received". */
const ORBITAL_CT_LABEL_OVERRIDES = { dicom_received: 'DICOM received date' }

/**
 * How old the study is **at the report date** — not at the DICOM receipt.
 *
 * 🔴 This was wrong first time and the reference itself disproved it. On
 * O3D-2026-06-18-0001 the study date is 2026-05-11 and the DICOM was received
 * 2026-05-19 — eight days — yet the delivered document says "Old scan" and its
 * waiver says the scan was "more than 30 days old". The reference's report date
 * is 2026-Jul-02: 52 days after the study.
 *
 * Confirmed against the existing implementation rather than inferred:
 * `backend/src/routes/render.js` computes `study_age_days` as
 * `Date.now() - studyDate`, and `sseReport.js` reddens the date on
 * `study_age_days > 30`. So the reference point is WHEN THE REPORT IS MADE, and
 * `dicom_received` is only ever a printed field.
 *
 * `asOf` is the report date when one is given, so regenerating a 2026 case in
 * 2028 does not silently reclassify its scan as older — the age belongs to the
 * report, like the copyright year.
 *
 * @returns {number|null} days, or null when the study date is unusable
 */
export function studyAgeDays(ctInfo, asOf = undefined) {
  const study = Date.parse(ctInfo?.study_date ?? '')
  if (!Number.isFinite(study)) return null
  // An upstream-supplied age wins: it is what the SSE report already printed
  // for this scan, and two documents disagreeing about one scan's age is worse
  // than either being a day out.
  if (Number.isFinite(Number(ctInfo?.study_age_days))) return Number(ctInfo.study_age_days)
  const ref = asOf != null ? Date.parse(asOf) : Date.now()
  if (!Number.isFinite(ref)) return null
  return Math.round((ref - study) / 86400000)
}

/** The 30-day threshold, shared with sseReport.js. One rule, two documents. */
export const OLD_SCAN_DAYS = 30

/**
 * Is this an "old scan"?
 *
 * The operator may override, because only they know whether an undated or
 * oddly-dated series is genuinely stale — and `false` must be distinguishable
 * from "not stated": an explicit false wins, `undefined` derives.
 */
export function isOldScan(ctInfo, override, asOf = undefined) {
  if (override === true || override === false) return override
  const days = studyAgeDays(ctInfo, asOf)
  return days != null && days > OLD_SCAN_DAYS
}

const SIDE_LABEL = { left: 'left', right: 'right' }

/** 'orbital_volume_left' → 'left'. */
const sideOfRole = (role) => (role === 'orbital_volume_left' ? 'left'
  : role === 'orbital_volume_right' ? 'right' : null)

/**
 * Which side did the plan act on?
 *
 * Whichever side has a post-plan orbital volume. Both sides having one is a real
 * ambiguity — a bilateral case, or a mis-named file — and is reported rather than
 * resolved, because picking one would put the wrong side's figure under a
 * confident label.
 */
export function resolvePlannedSide({ resolve, resolveCase }) {
  // Which side the plan acted on is a fact about the CASE — it is decided by
  // which post-plan volume the case holds, and the pages that state it need not
  // show either volume. Resolved page-scoped, a page given only the difference
  // model lost the mirror direction ("Right to left") silently, and a page given
  // one volume stated a direction on a case holding both, which page 3
  // explicitly refuses to call.
  const r = resolveCase ?? resolve
  const left = r('orbital_volume_left', 'postplan')
  const right = r('orbital_volume_right', 'postplan')
  if (left && right) return { side: null, mesh: null, both: true }
  if (left) return { side: 'left', mesh: left, both: false }
  if (right) return { side: 'right', mesh: right, both: false }
  return { side: null, mesh: null, both: false }
}

const skipNote = (notes, title, reason, missing = []) =>
  notes.push({ page: title, kind: 'skipped', reason: `not built — ${reason}`, missing })

/** Volume for a resolved mesh, from the supplied analysis. */
const volumeFor = (volumes, mesh) =>
  (mesh ? volumes[`${mesh.role}/${mesh.stage}`] ?? null : null)

export const ORBITAL_RECIPES = [
  // ── page 1 ────────────────────────────────────────────────────────────────
  {
    id: 'preview',
    title: 'Preview Image',
    kind: 'custom',
    compose(ctx) {
      const { doc, notes, ctInfo, waiver, resolve, addScene, partFor, nextId,
        impliedFrame, recipe, meta } = ctx
      const old = isOldScan(ctInfo, meta?.oldScan, meta?.dateStr)
      const rows = ORBITAL_CT_ROW_KEYS
        .map((key) => {
          const value = ctInfo?.[key]
          if (value == null || value === '') return null
          const label = ORBITAL_CT_LABEL_OVERRIDES[key] ?? CT_LABEL.get(key) ?? key
          // The study date prints RED when the scan is old — the reference does
          // this, and it is the one visual cue on page 1 that the plan rests on
          // a stale study.
          const style = (key === 'study_date' && old) ? { color: RED, bold: true } : undefined
          return style ? [label, String(value), style] : [label, String(value)]
        })
        .filter(Boolean)

      if (!rows.length) {
        skipNote(notes, recipe.title, 'no CT data was entered for this case', ['CT data'])
        return
      }

      const images = []
      const skull = resolve('skull', 'native')
      if (skull) {
        const parts = [partFor(skull)]
        images.push({
          kind: 'scene',
          sceneId: addScene('Native Skull', parts, 'plan', impliedFrame(parts)),
          view: 'ISO',
        })
      }

      const page = newInfoPage(nextId('p'), {
        groups: [{ title: `Skull data${old ? ' - Old scan' : ''}`, rows }],
        images,
      })
      page.title = 'CT data information'
      // Page 1's header reads "Preview Image" where pages 2-6 read the document
      // title. Measured: same baseline, same centre, different string.
      page.headerTitle = 'Preview Image'
      const wv = waiverBlockFor(waiver)
      if (wv) page.waiverBlock = wv
      page.recipeId = recipe.id
      doc.pages.push(page)
    },
  },

  // ── page 2 ────────────────────────────────────────────────────────────────
  {
    id: 'native-skull',
    title: 'Native skull',
    kind: 'custom',
    compose(ctx) {
      const { notes, ctInfo, meta, resolve, addScene, partFor, impliedFrame,
        pushSixViewPage, recipe, candidates } = ctx
      const skull = resolve('skull', 'native')
      if (!skull) {
        const n = candidates('skull', 'native').length
        skipNote(notes, recipe.title,
          n > 1 ? `Skull (native) is ambiguous (${n} candidates — choose one)`
            : 'Skull (native) is not in this case', ['Skull (native)'])
        return
      }
      const parts = [partFor(skull)]
      const sceneId = addScene('Native Skull', parts, 'plan', impliedFrame(parts))
      const suffix = isOldScan(ctInfo, meta?.oldScan, meta?.dateStr) ? ' - Old scan' : ''
      pushSixViewPage(sceneId, { tc: `Native skull${suffix}` }, recipe.id)
    },
  },

  // ── page 3 ────────────────────────────────────────────────────────────────
  {
    id: 'orbital-volumes',
    title: 'Orbital volume calculation',
    kind: 'custom',
    compose(ctx) {
      const { doc, notes, volumes, resolve, addScene, partFor, nextId,
        impliedFrame, recipe } = ctx
      const skull = resolve('skull', 'native')
      const natLeft = resolve('orbital_volume_left', 'native')
      const natRight = resolve('orbital_volume_right', 'native')
      const planned = resolvePlannedSide(ctx)

      if (planned.both) {
        skipNote(notes, recipe.title,
          'both orbits carry a post-plan volume — cannot tell which side the plan acted on; '
          + 'check the filenames', ['post-plan orbital volume'])
        return
      }
      const missing = []
      // 🔴 The bone behind ALL THREE volumes is the NATIVE skull, including the
      // green post-plan one. Confirmed by Deepak 2026-09-12, and visible in the
      // reference's own legend and colouring.
      if (!skull) missing.push('Skull (native)')
      if (!natLeft && !natRight) missing.push('a native orbital volume')
      if (!planned.mesh) missing.push('a post-plan orbital volume')
      if (missing.length) {
        skipNote(notes, recipe.title, `${missing.join(', ')} not resolved for this case`, missing)
        return
      }

      // Legend rows, each label DERIVED from the mesh that resolved — never a
      // fixed string. RC-2: a hardcoded "left" here is how a right-side case
      // would report one orbit's volume under the other orbit's name.
      const legend = []
      const rowFor = (mesh, colorHex, labelPrefix) => {
        const v = volumeFor(volumes, mesh)
        if (!mesh) return
        if (!v) { missing.push(`volume for ${mesh.name}`); return }
        legend.push({
          color: hexToRgb(colorHex),
          label: `${labelPrefix} ${SIDE_LABEL[sideOfRole(mesh.role)] ?? ''} orbit`.replace(/\s+/g, ' ').trim(),
          value: formatCc(v.cc),
        })
        if (v.watertight === false) {
          notes.push({
            page: recipe.title, kind: 'warning',
            reason: watertightWarning(mesh.name, v.cc),
            missing: [],
          })
        }
      }
      rowFor(natRight, ORBITAL_VOLUME_COLORS.nativeRightOrbit.albedo, 'Native')
      rowFor(natLeft, ORBITAL_VOLUME_COLORS.nativeLeftOrbit.albedo, 'Native')
      rowFor(planned.mesh, ORBITAL_VOLUME_COLORS.postPlanOrbit.albedo, 'Post plan')

      if (missing.length) {
        skipNote(notes, recipe.title,
          `${missing.join(', ')} — the volumes are computed from the meshes, so a page cannot be `
          + 'drawn without them', missing)
        return
      }

      // Three scenes: the natives together from two isometries, then the
      // post-plan volume in the same bone. All in the native skull, so the three
      // pictures are directly comparable.
      const skullPart = partFor(skull)
      // The volumes are drawn TRANSLUCENT, as the reference draws them: the
      // orbital walls read through them, which is what shows the solid sitting
      // inside the orbit rather than in front of it. Measured on the reference's
      // page 3 — bone detail is visible through all three volumes.
      const volumePart = (m) => ({ ...partFor(m), opacity: VOLUME_OPACITY })
      const nativeParts = [skullPart, ...[natRight, natLeft].filter(Boolean).map(volumePart)]
      const planParts = [skullPart, volumePart(planned.mesh)]
      const nativeScene = addScene('Native orbital volumes', nativeParts, 'orbit',
        impliedFrame(nativeParts))
      const planScene = addScene('Post plan orbital volume', planParts, 'orbit',
        impliedFrame(planParts))

      // The reference shows the natives from TWO oblique angles and the
      // post-plan volume from one. We ship the two that READ.
      //
      // The renderer can draw a second oblique (`ISO2`, opt-in via extraViews)
      // and it is tested — but its direction is not settled. Three were tried on
      // the real case and all three hide the volumes, because they are enclosed
      // and only a camera looking steeply into the export's cut-away top sees
      // them. Choosing that angle is a judgement about a clinical figure and
      // belongs to the design team; see EXTRA_VIEW_DEFS in meshRenderer.js for
      // what was measured. Adding it back is one entry in this list.
      //
      // Shipping the third panel with the volumes invisible would be worse than
      // shipping two: the page would look complete and show nothing.
      const items = [
        { sceneId: nativeScene, view: 'ISO', annotations: [] },
        { sceneId: planScene, view: 'ISO', annotations: [] },
      ]
      for (const it of items) {
        if (!isRenderableView(it.view)) {
          notes.push({
            page: recipe.title, kind: 'incomplete', missing: [it.view],
            reason: `built WITHOUT the ${it.view} view — the renderer cannot produce it. `
              + 'Every volume figure is still shown.',
          })
        }
      }
      notes.push({
        page: recipe.title,
        kind: 'incomplete',
        reason: 'two views of the orbital volumes, where the delivered report has three. The '
          + 'third angle needs a camera direction from the design team — every angle tried so '
          + 'far hides the volumes behind the orbital rims. All three volume figures are shown.',
        missing: ['second oblique angle'],
      })
      const page = newGalleryPage(nextId('p'), { items })
      page.captions.tc = recipe.title
      page.legend = legend
      page.frame = 'boxed'
      page.recipeId = recipe.id
      doc.pages.push(page)
    },
  },

  // ── page 4 ────────────────────────────────────────────────────────────────
  {
    id: 'bilateral-orbital',
    title: 'Bilateral orbital model with mirrored orbital floor and medial wall fused',
    kind: 'custom',
    compose(ctx) {
      const { doc, notes, resolve, addScene, partFor, nextId, impliedFrame,
        recipe, candidates } = ctx
      const recon = resolve('skull', 'postplan')
      if (!recon) {
        const n = candidates('skull', 'postplan').length
        skipNote(notes, recipe.title,
          n > 1 ? `Skull (postplan) is ambiguous (${n} candidates — choose one)`
            : 'the reconstructed skull is not in this case', ['Skull (postplan)'])
        return
      }
      const parts = [partFor(recon)]
      const sceneId = addScene('Reconstructed Skull', parts, 'plan', impliedFrame(parts))
      const page = sixViewGridPage(nextId('p'), sceneId)
      page.captions.tc = recipe.title
      // The lateral views are ZOOMED to the orbit on the reference — measured,
      // and the reason `cell.crop` exists (ADR 0009 chose a 2D crop over a second
      // camera so the shared renderer cannot be perturbed). Centre-biased upward
      // because the orbit sits above the mid-height of a lateral skull view.
      // Fractions, so a change of render resolution does not re-author the page.
      const CROP = { x: 0.27, y: 0.18, w: 0.46, h: 0.46 }
      for (const cell of page.cells ?? []) {
        if (cell.view === 'LL' || cell.view === 'RL') cell.crop = { ...CROP }
      }
      page.recipeId = recipe.id
      doc.pages.push(page)
    },
  },

  // ── page 5 ────────────────────────────────────────────────────────────────
  {
    id: 'difference-model',
    title: 'Orbital difference model',
    kind: 'custom',
    compose(ctx) {
      const { notes, resolveByRole, addScene, partFor, impliedFrame,
        pushSixViewPage, recipe } = ctx
      const diff = resolveByRole('orbital_difference')
      if (!diff) {
        skipNote(notes, recipe.title,
          'the orbital difference model is not in this case', ['Orbital Difference Model'])
        return
      }
      const planned = resolvePlannedSide(ctx)
      const parts = [partFor(diff)]
      const sceneId = addScene('Orbital Difference Model', parts, null, impliedFrame(parts))
      // "Right to left" on the reference — the mirror direction, which follows
      // the side the plan acted on. Stated only when that side is known.
      const from = planned.side === 'left' ? 'Right to left'
        : planned.side === 'right' ? 'Left to right' : null
      pushSixViewPage(sceneId,
        { tc: from ? `${recipe.title} - ${from}` : recipe.title }, recipe.id)
    },
  },

  // ── page 6 ────────────────────────────────────────────────────────────────
  {
    id: 'difference-on-trial',
    title: 'Orbital difference model on trial fitment model',
    kind: 'custom',
    compose(ctx) {
      const { doc, notes, resolveByRole, addScene, partFor, nextId, impliedFrame,
        recipe } = ctx
      const diff = resolveByRole('orbital_difference')
      const trial = resolveByRole('trial_fitment_model')
      const missing = []
      if (!diff) missing.push('Orbital Difference Model')
      if (!trial) missing.push('Trial Fitment Model')
      if (missing.length) {
        skipNote(notes, recipe.title, `${missing.join(' and ')} not in this case`, missing)
        return
      }
      const planned = resolvePlannedSide(ctx)
      const side = planned.side ? ` - ${SIDE_LABEL[planned.side]} orbit` : ''
      // The difference model renders as a fabricated appliance against the
      // neutral lit-sphere; the trial model is bone. partFor derives both from
      // the roles, so nothing is overridden here.
      const parts = [partFor(trial), partFor(diff)]
      const sceneId = addScene('Difference on Trial Fitment', parts, null, impliedFrame(parts))
      const page = newGalleryPage(nextId('p'), {
        items: [{
          sceneId,
          view: 'ISO',
          // Two leader callouts, authored by the recipe as DEFAULTS. They are
          // ordinary annotations, so the operator edits or moves them in
          // PageEditor like any other — applyEdits re-applies on top.
          // Normalised to the item frame.
          annotations: [
            { id: 'a-diff', kind: 'leader', x1: 0.34, y1: 0.80, x2: 0.30, y2: 0.52 },
            { id: 't-diff', kind: 'text', x1: 0.34, y1: 0.84, text: 'Orbital difference model' },
            { id: 'a-trial', kind: 'leader', x1: 0.68, y1: 0.74, x2: 0.62, y2: 0.60 },
            {
              id: 't-trial', kind: 'text', x1: 0.68, y1: 0.78,
              text: `Trial fitment model${side}`,
            },
          ],
        }],
      })
      page.captions.tc = `${recipe.title}${side}`
      page.frame = 'boxed'
      page.recipeId = recipe.id
      doc.pages.push(page)
    },
  },
]

/** The views this template renders, so the caller can ask for ISO_L opt-in. */
export const ORBITAL_VIEWS = [...GRID_VIEWS, 'ISO', 'ISO2']
